import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRole,
  deleteRole,
  fetchPermissions,
  updateRole,
} from '../../lib/identityRegistry';
import { ConfirmDialog } from '../ConfirmDialog';
import type { RoleRecord } from '../../types/registry';

interface RoleManagerProps {
  roles: RoleRecord[];
}

export function RoleManager({ roles }: RoleManagerProps): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [editingRole, setEditingRole] = useState<RoleRecord | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RoleRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const permissionsQuery = useQuery({
    queryKey: ['permissions'],
    queryFn: fetchPermissions,
  });
  const permissionOptions = permissionsQuery.data?.permissions ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['roles'] });

  function resetCreateForm() {
    setRoleName('');
    setPermissions([]);
    setCreateError(null);
  }

  function closeCreate() {
    if (createMutation.isPending) return;
    setCreateOpen(false);
    resetCreateForm();
  }

  const createMutation = useMutation({
    mutationFn: createRole,
    onSuccess: async () => {
      await invalidate();
      setCreateOpen(false);
      resetCreateForm();
      setError(null);
    },
    onError: (mutationError) => {
      setCreateError(mutationError instanceof Error ? mutationError.message : 'Create failed');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      roleId,
      payload,
    }: {
      roleId: string;
      payload: { role_name: string; permissions: string[] };
    }) => updateRole(roleId, payload),
    onSuccess: async () => {
      await invalidate();
      setEditingRole(null);
      setError(null);
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'Update failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: async () => {
      await invalidate();
      setPendingDelete(null);
    },
    onError: (mutationError) => {
      setError(mutationError instanceof Error ? mutationError.message : 'Delete failed');
      setPendingDelete(null);
    },
  });

  function togglePermission(permission: string, checked: boolean) {
    setPermissions((current) => {
      if (checked) return [...current, permission];
      return current.filter((entry) => entry !== permission);
    });
  }

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    createMutation.mutate({ role_name: roleName, permissions });
  }

  function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingRole) return;
    updateMutation.mutate({
      roleId: editingRole.role_id,
      payload: { role_name: editingRole.role_name, permissions: editingRole.permissions },
    });
  }

  return (
    <>
      <div className="panel-toolbar">
        <h2>Roles</h2>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setError(null);
            resetCreateForm();
            setCreateOpen(true);
          }}
        >
          Create role
        </button>
      </div>

      {error ? <div className="form-error">{error}</div> : null}
      {!roles.length ? <div className="empty-state">No custom roles yet.</div> : null}
      {roles.length ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Permissions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.role_id}>
                <td>
                  {role.role_name}
                  {role.is_system_default ? ' (system)' : ''}
                </td>
                <td>{role.permissions.join(', ') || '—'}</td>
                <td>
                  <div className="table-actions">
                    {role.is_system_default ? (
                      <span className="hint-text">Read-only</span>
                    ) : (
                      <>
                        <button type="button" onClick={() => setEditingRole(role)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            setError(null);
                            setPendingDelete(role);
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {createOpen ? (
        <div className="modal-backdrop" onClick={closeCreate}>
          <div className="modal-card modal-card--wide" onClick={(event) => event.stopPropagation()}>
            <h2>Create role</h2>
            <p className="modal-card__copy">Choose a name and the permissions this role can use.</p>
            <form className="role-form" onSubmit={handleCreate}>
              <label className="form-field">
                <span>Role name</span>
                <input
                  value={roleName}
                  onChange={(event) => setRoleName(event.target.value)}
                  required
                  autoFocus
                />
              </label>
              {permissionsQuery.isLoading ? (
                <p className="hint-text">Loading permissions...</p>
              ) : permissionsQuery.error ? (
                <div className="form-error">
                  {permissionsQuery.error instanceof Error
                    ? permissionsQuery.error.message
                    : 'Failed to load permissions'}
                </div>
              ) : !permissionOptions.length ? (
                <p className="hint-text">No assignable permissions available.</p>
              ) : (
                <div className="permission-grid">
                  {permissionOptions.map((permission) => (
                    <label key={permission}>
                      <input
                        type="checkbox"
                        checked={permissions.includes(permission)}
                        onChange={(event) => togglePermission(permission, event.target.checked)}
                      />
                      {permission}
                    </label>
                  ))}
                </div>
              )}
              {createError ? <div className="form-error">{createError}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="ghost-button"
                  type="button"
                  disabled={createMutation.isPending}
                  onClick={closeCreate}
                >
                  Cancel
                </button>
                <button
                  className="primary-button"
                  disabled={createMutation.isPending || !permissionOptions.length}
                  type="submit"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editingRole ? (
        <div className="drawer-backdrop" onClick={() => setEditingRole(null)}>
          <aside className="drawer" onClick={(event) => event.stopPropagation()}>
            <h2>Edit role</h2>
            <form className="role-form" onSubmit={handleUpdate}>
              <label className="form-field">
                <span>Role name</span>
                <input
                  value={editingRole.role_name}
                  onChange={(event) =>
                    setEditingRole({ ...editingRole, role_name: event.target.value })
                  }
                  required
                  disabled={editingRole.is_system_default}
                />
              </label>
              {!permissionOptions.length ? (
                <p className="hint-text">No assignable permissions available.</p>
              ) : (
                <div className="permission-grid">
                  {permissionOptions.map((permission) => (
                    <label key={permission}>
                      <input
                        type="checkbox"
                        checked={editingRole.permissions.includes(permission)}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...editingRole.permissions, permission]
                            : editingRole.permissions.filter((entry) => entry !== permission);
                          setEditingRole({ ...editingRole, permissions: next });
                        }}
                      />
                      {permission}
                    </label>
                  ))}
                </div>
              )}
              <div className="drawer__actions">
                <button className="primary-button" disabled={updateMutation.isPending} type="submit">
                  {updateMutation.isPending ? 'Saving...' : 'Save role'}
                </button>
                <button className="ghost-button" type="button" onClick={() => setEditingRole(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </aside>
        </div>
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title="Delete role"
          message={`Delete role "${pendingDelete.role_name}"? This cannot be undone.`}
          busy={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(pendingDelete.role_id)}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
